import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp,
  onSnapshot,
  Unsubscribe,
  getFirestore,
  limit,
} from 'firebase/firestore';
import { firebaseApp } from './firebaseConfig';

const db = getFirestore(firebaseApp);

export interface AuditLogEntry {
  id?: string;
  // Action type
  action: 'play_rabbi_audio';
  // Who performed the action
  userId: string;
  userName?: string;
  userEmail?: string;
  // Rabbi whose audio was played
  rabbiId: string;
  rabbiInitials?: string;
  // Verse details
  parashaKey: string;
  parashaHebrew?: string;
  book: string | number;
  chapter: number;
  pasuk: number;
  verseIndex: number;
  // Timestamp
  createdAt: Timestamp;
  // Additional metadata
  nusach?: string;
}

export interface AuditStats {
  totalPlays: number;
  uniqueListeners: number;
  playsByVerse: Record<string, number>;
  playsByParasha: Record<string, number>;
  recentPlays: AuditLogEntry[];
  dailyPlays: { date: string; count: number }[];
}

/**
 * Log when a student plays rabbi audio
 */
export async function logRabbiAudioPlay(
  entry: Omit<AuditLogEntry, 'id' | 'createdAt' | 'action'>,
): Promise<string | undefined> {
  try {
    const auditRef = collection(db, 'audit');
    const docRef = await addDoc(auditRef, {
      ...entry,
      action: 'play_rabbi_audio' as const,
      createdAt: Timestamp.now(),
    });
    console.log('Audit log created:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('Error logging audit entry:', error);
    return undefined;
  }
}

export interface MissingVerseNotifyEntry {
  userId: string;
  parashaKey: string;
  book: string | number;
  chapter: number;
  pasuk: number;
  verseIndex: number;
  nusach?: string;
}

/**
 * Log when a student asks to be notified once a missing verse's audio is ready
 */
export async function logMissingVerseNotifyRequest(
  entry: MissingVerseNotifyEntry,
): Promise<string | undefined> {
  try {
    const notifyRef = collection(db, 'missingVerseNotifyRequests');
    const docRef = await addDoc(notifyRef, {
      ...entry,
      createdAt: Timestamp.now(),
    });
    return docRef.id;
  } catch (error) {
    console.error('Error logging missing verse notify request:', error);
    return undefined;
  }
}

/**
 * Get audit statistics for a specific rabbi
 */
export async function getAuditStatsForRabbi(rabbiId: string): Promise<AuditStats> {
  try {
    const auditRef = collection(db, 'audit');
    const q = query(
      auditRef,
      where('rabbiId', '==', rabbiId),
      where('action', '==', 'play_rabbi_audio'),
      orderBy('createdAt', 'desc'),
    );
    const querySnapshot = await getDocs(q);

    const entries: AuditLogEntry[] = [];
    querySnapshot.forEach((docSnap) => {
      entries.push({
        id: docSnap.id,
        ...(docSnap.data() as Omit<AuditLogEntry, 'id'>),
      });
    });

    // Calculate statistics
    const uniqueListeners = new Set(entries.map((e) => e.userId)).size;
    const playsByVerse: Record<string, number> = {};
    const playsByParasha: Record<string, number> = {};
    const dailyPlaysMap: Record<string, number> = {};

    entries.forEach((entry) => {
      // Count by verse
      const verseKey = `${entry.book}:${entry.chapter}:${entry.pasuk}`;
      playsByVerse[verseKey] = (playsByVerse[verseKey] || 0) + 1;

      // Count by parasha
      const parashaKey = entry.parashaKey || 'unknown';
      playsByParasha[parashaKey] = (playsByParasha[parashaKey] || 0) + 1;

      // Count by date
      const date = entry.createdAt?.toDate
        ? entry.createdAt.toDate().toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0];
      dailyPlaysMap[date] = (dailyPlaysMap[date] || 0) + 1;
    });

    // Convert daily plays to sorted array (last 30 days)
    const dailyPlays = Object.entries(dailyPlaysMap)
      .map(([date, count]) => ({ date, count }))
      .sort((a, b) => b.date.localeCompare(a.date))
      .slice(0, 30);

    return {
      totalPlays: entries.length,
      uniqueListeners,
      playsByVerse,
      playsByParasha,
      recentPlays: entries.slice(0, 20),
      dailyPlays,
    };
  } catch (error) {
    console.error('Error fetching audit stats for rabbi:', error);
    return {
      totalPlays: 0,
      uniqueListeners: 0,
      playsByVerse: {},
      playsByParasha: {},
      recentPlays: [],
      dailyPlays: [],
    };
  }
}

/**
 * Subscribe to real-time audit statistics for a rabbi
 */
export function subscribeToRabbiAuditStats(
  rabbiId: string,
  onUpdate: (stats: AuditStats) => void,
): Unsubscribe {
  const auditRef = collection(db, 'audit');
  const q = query(
    auditRef,
    where('rabbiId', '==', rabbiId),
    where('action', '==', 'play_rabbi_audio'),
    orderBy('createdAt', 'desc'),
    limit(500), // Limit for performance
  );

  return onSnapshot(q, (snapshot) => {
    const entries: AuditLogEntry[] = [];
    snapshot.forEach((docSnap) => {
      entries.push({
        id: docSnap.id,
        ...(docSnap.data() as Omit<AuditLogEntry, 'id'>),
      });
    });

    // Calculate statistics
    const uniqueListeners = new Set(entries.map((e) => e.userId)).size;
    const playsByVerse: Record<string, number> = {};
    const playsByParasha: Record<string, number> = {};
    const dailyPlaysMap: Record<string, number> = {};

    entries.forEach((entry) => {
      const verseKey = `${entry.book}:${entry.chapter}:${entry.pasuk}`;
      playsByVerse[verseKey] = (playsByVerse[verseKey] || 0) + 1;

      const parashaKey = entry.parashaKey || 'unknown';
      playsByParasha[parashaKey] = (playsByParasha[parashaKey] || 0) + 1;

      const date = entry.createdAt?.toDate
        ? entry.createdAt.toDate().toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0];
      dailyPlaysMap[date] = (dailyPlaysMap[date] || 0) + 1;
    });

    const dailyPlays = Object.entries(dailyPlaysMap)
      .map(([date, count]) => ({ date, count }))
      .sort((a, b) => b.date.localeCompare(a.date))
      .slice(0, 30);

    onUpdate({
      totalPlays: entries.length,
      uniqueListeners,
      playsByVerse,
      playsByParasha,
      recentPlays: entries.slice(0, 20),
      dailyPlays,
    });
  });
}

/**
 * Get recent plays for a specific rabbi (last N entries)
 */
export async function getRecentPlaysForRabbi(
  rabbiId: string,
  limitCount = 10,
): Promise<AuditLogEntry[]> {
  try {
    const auditRef = collection(db, 'audit');
    const q = query(
      auditRef,
      where('rabbiId', '==', rabbiId),
      where('action', '==', 'play_rabbi_audio'),
      orderBy('createdAt', 'desc'),
      limit(limitCount),
    );
    const querySnapshot = await getDocs(q);

    const entries: AuditLogEntry[] = [];
    querySnapshot.forEach((docSnap) => {
      entries.push({
        id: docSnap.id,
        ...(docSnap.data() as Omit<AuditLogEntry, 'id'>),
      });
    });

    return entries;
  } catch (error) {
    console.error('Error fetching recent plays for rabbi:', error);
    return [];
  }
}

/**
 * Get all audit entries for a specific rabbi (for getting all students)
 */
export async function getAllAuditEntriesForRabbi(rabbiId: string): Promise<AuditLogEntry[]> {
  try {
    const auditRef = collection(db, 'audit');
    const q = query(
      auditRef,
      where('rabbiId', '==', rabbiId),
      where('action', '==', 'play_rabbi_audio'),
      orderBy('createdAt', 'desc'),
    );
    const querySnapshot = await getDocs(q);

    const entries: AuditLogEntry[] = [];
    querySnapshot.forEach((docSnap) => {
      entries.push({
        id: docSnap.id,
        ...(docSnap.data() as Omit<AuditLogEntry, 'id'>),
      });
    });

    return entries;
  } catch (error) {
    console.error('Error fetching all audit entries for rabbi:', error);
    return [];
  }
}
